import { auth } from '../../firebase';

const API_BASE_URL = `${process.env.REACT_APP_API_DOMAIN}/api/v1`;

/**
 * Get Firebase auth token
 * @returns {Promise<string>} Bearer token
 */
const getAuthToken = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return await user.getIdToken();
};

/**
 * Helper to handle API responses and parse backend errors
 * @param {Response} response - The fetch response object
 * @param {string} defaultErrorMessage - fallback error message
 * @returns {Promise<any>} - Parsed JSON response
 */
const handleResponse = async (response, defaultErrorMessage) => {
  if (!response.ok) {
    let errorMessage = defaultErrorMessage || `Request failed: ${response.statusText}`;
    try {
      const errorData = await response.json();
      // Backend returns: { timestamp, status, error, message }
      // We prioritize 'message' as per requirement
      if (errorData && errorData.message) {
        errorMessage = errorData.message;
      }
    } catch (e) {
      // If response is not JSON or parsing fails, fall back to default/statusText
      if (response.statusText) {
        errorMessage = `${defaultErrorMessage}: ${response.statusText}`;
      }
    }
    throw new Error(errorMessage);
  }
  // For successful responses that might be empty (e.g. 204), handle carefully if needed.
  // Assuming most successful responses here return JSON.
  return response.json();
};

/**
 * Make authenticated API request
 * @param {string} endpoint - API endpoint
 * @param {object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
const authenticatedFetch = async (endpoint, options = {}) => {
  const token = await getAuthToken();

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  return fetch(`${API_BASE_URL}${endpoint}`, {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  });
};

/**
 * Create a new form
 * @param {object} formData - Form data without questions
 * @returns {Promise<object>} Created form
 */
export const createForm = async (formData) => {
  const response = await authenticatedFetch('/manage/forms', {
    method: 'POST',
    body: JSON.stringify(formData),
  });
  return handleResponse(response, 'Failed to create form');
};

/**
 * Update an existing form
 * @param {string} formId - Form ID
 * @param {object} formData - Complete form data with all questions
 * @returns {Promise<object>} Updated form
 */
export const updateForm = async (formId, formData) => {
  const response = await authenticatedFetch(`/manage/forms/${formId}`, {
    method: 'PUT',
    body: JSON.stringify(formData),
  });
  return handleResponse(response, 'Failed to update form');
};

/**
 * Get form by ID (authenticated - for form management)
 * @param {string} formId - Form ID
 * @returns {Promise<object>} Form data
 */
export const getForm = async (formId) => {
  const response = await authenticatedFetch(`/manage/forms/${formId}`);
  return handleResponse(response, 'Failed to get form');
};

/**
 * Get public form by ID (unauthenticated - for form filling)
 * Note: Backend should provide /api/v1/public/forms/{formId} endpoint
 * that returns published forms without authentication requirements
 * @param {string} formId - Form ID
 * @returns {Promise<object>} Form data
 */
export const getPublicForm = async (formId) => {
  const response = await fetch(`${API_BASE_URL}/p/forms/${formId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Form not found or not published');
    }
    // Use the same helper logic for public API errors
    try {
      const errorData = await response.json();
      if (errorData && errorData.message) {
        throw new Error(errorData.message);
      }
    } catch (e) {
      if (e.message && e.message !== 'Unexpected end of JSON input') {
        // If we already threw the message from inner try, rethrow
        throw e;
      }
    }
    throw new Error(`Failed to load form: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Get all forms for the current user (paginated)
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @returns {Promise<object>} Paginated response { content, totalPages, totalElements, ... }
 */
export const getForms = async (page = 0, size = 10) => {
  const response = await authenticatedFetch(`/manage/forms?page=${page}&size=${size}`);
  return handleResponse(response, 'Failed to get forms');
};

/**
 * Publish a form
 * @param {string} formId - Form ID
 * @returns {Promise<object>} Updated form
 */
export const publishForm = async (formId) => {
  const response = await authenticatedFetch(`/manage/forms/${formId}/publish`, {
    method: 'POST',
  });
  return handleResponse(response, 'Failed to publish form');
};

/**
 * Close a form
 * @param {string} formId - Form ID
 * @returns {Promise<object>} Updated form
 */
export const closeForm = async (formId) => {
  const response = await authenticatedFetch(`/manage/forms/${formId}/close`, {
    method: 'POST',
  });
  return handleResponse(response, 'Failed to close form');
};

/**
 * Schedule form closure
 * @param {string} formId - Form ID
 * @param {string|null} scheduledTime - ISO-8601 string or null to remove schedule
 * @returns {Promise<object>} Result
 */
export const scheduleFormClosure = async (formId, scheduledTime) => {
  let url = `/manage/forms/${formId}/schedule-closure`;
  if (scheduledTime) {
    url += `?scheduledTime=${encodeURIComponent(scheduledTime)}`;
  }
  const response = await authenticatedFetch(url, {
    method: 'POST',
  });
  return handleResponse(response, 'Failed to schedule form closure');
};

/**
 * Submit form response (public)
 * @param {string} formId - Form ID
 * @param {object} responseData - Form response data
 * @returns {Promise<object>} Submission result
 */
export const submitFormResponse = async (formId, responseData) => {
  const response = await fetch(`${API_BASE_URL}/p/forms/${formId}/submit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(responseData),
  });
  return handleResponse(response, 'Failed to submit form');
};

/**
 * Verify OTP for form submission
 * @param {object} data - { token, otp }
 * @returns {Promise<object>} Verification result
 */
export const verifyOtp = async (data) => {
  const response = await fetch(`${API_BASE_URL}/p/forms/verify-otp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  return handleResponse(response, 'Failed to verify OTP');
};

/**
 * Get form responses (paginated)
 * @param {string} formId - Form ID
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @returns {Promise<object>} Paginated responses
 */
export const getFormResponses = async (formId, page = 0, size = 10) => {
  const response = await authenticatedFetch(`/manage/forms/${formId}/submissions?page=${page}&size=${size}`);
  return handleResponse(response, 'Failed to get responses');
};

/**
 * Export form responses (Async - emails the file)
 * @param {string} formId - Form ID
 * @returns {Promise<object>} Success message
 */
export const exportFormResponses = async (formId) => {
  const response = await authenticatedFetch(`/manage/forms/${formId}/export-async`);
  return handleResponse(response, 'Failed to initiate export');
};

/**
 * Check if an email can fill a specific form
 * @param {string} formId - Form ID
 * @param {string} email - Email to check
 * @returns {Promise<boolean>} True if can fill, false otherwise
 */
export const checkCanFillForm = async (formId, email) => {
  // Assuming the endpoint is public and under /p/forms/{formId}/can-i-fill
  const response = await fetch(`${API_BASE_URL}/p/forms/${formId}/can-i-fill?email=${encodeURIComponent(email)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to check eligibility: ${response.statusText}`);
  }

  const data = await response.json();
  return data.canIFill;
};

/**
 * Duplicate a form
 * @param {string} formId - Form ID to duplicate
 * @returns {Promise<object>} Duplicated form
 */
export const duplicateForm = async (formId) => {
  const response = await authenticatedFetch(`/manage/forms/${formId}/duplicate`, {
    method: 'POST',
  });
  return handleResponse(response, 'Failed to duplicate form');
};

/**
 * Use AI to edit an existing form based on a natural language prompt
 * @param {string} prompt - User's natural language instruction
 * @param {object} currentForm - Current form state (title, description, settings, questions)
 * @returns {Promise<object>} Updated form data (FormRequestDto structure)
 */
export const editFormWithAi = async (prompt, currentForm, previousAttemptJson = null, validationErrors = null) => {
  const response = await authenticatedFetch('/manage/forms/ai-edit', {
    method: 'POST',
    body: JSON.stringify({ prompt, currentForm, previousAttemptJson, validationErrors }),
  });
  return handleResponse(response, 'Failed to update form using AI');
};

/**
 * Upload a file for a specific file-upload question on a form.
 * Returns the Cloudinary URL of the uploaded file.
 * @param {string} formId - Form ID
 * @param {string} questionId - Question ID
 * @param {File} file - The file to upload
 * @returns {Promise<{ url: string }>}
 */
export const uploadFile = async (formId, questionId, file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(
    `${API_BASE_URL}/p/files/upload?formId=${encodeURIComponent(formId)}&questionId=${encodeURIComponent(questionId)}`,
    {
      method: 'POST',
      body: formData,
      // No Content-Type header — browser sets it automatically with the boundary for multipart
    }
  );
  return handleResponse(response, 'Failed to upload file');
};

/*
export const downloadFile = async (fileId, type) => {
  const response = await authenticatedFetch(
    `/manage/files/download/${encodeURIComponent(fileId)}?type=${encodeURIComponent(type)}`
  );

  if (!response.ok) {
    let message = `Download failed: ${response.status}`;

    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Response wasn't JSON
    }

    throw new Error(message);
  }

  return response;
};
*/

export const getShortLivedDownloadToken = async (fileId, type) => {
  const response = await authenticatedFetch(`/manage/files/download-token/${encodeURIComponent(fileId)}?type=${encodeURIComponent(type)}`);
  return handleResponse(response, 'Failed to download the file');
};

export const getFullShortLivedDownloadURL = (fileDownloadToken) => {
  return `${API_BASE_URL}/p/files/download/${fileDownloadToken}`
};